import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { Form1118SyncService } from './form-1118-sync.service';
import * as _ from 'lodash-es';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

@Component({
    selector: 'gtw-form-1118-sync',
    templateUrl: './form-1118-sync.component.html',
    styleUrls: ['./form-1118-sync.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class Form1118SyncComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // 🔴 V9: Flags to prevent duplicate initialization
  private isInitialized = false;
  private isLoading = false;

  filterParams: any = {};
  baseURLs: any = {};
  rowData: any = {};

  // Component properties
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  
  // Form data properties
  filteredTaxYearData: any[] = [];
  taxYearData: any[] = [];
  selectedTaxYear: string = '';
  TaxYearKey: string = '';
  curentyear: string = '';
  scenario: string = '';
  jcd_key: string = '';
  
  scenarioDropDown: any[] = [];
  selectedScenario: string = '';
  showscenario: boolean = false;
  
  filingGroupDropDown: any[] = [];
  selectedFilingGroup: string = '';
  showFilingGroup: boolean = false;
  
  filingEnityDetails: any[] = [];
  filingAllDetails: any[] = [];
  filerEntity: string | null = null;
  showFilerEntity: boolean = false;
  
  is_locked: any = null;
  showConfirmDialog: boolean = false;
  modalConfig: ConfirmationModalConfig = {
    title: '',
    message: ''
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private gtwSnackbarService: GtwSnackbarService,
    private form1118SyncService: Form1118SyncService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    this.isLoading = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
  }

  // 🔴 V9: Gate function to prevent duplicate initialization
  private tryInit() {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    
    // Check if ALL required data is present before initializing
    if (!this.globalService || !this.GENERAL_CONFIG || Object.keys(this.GENERAL_CONFIG).length === 0) {
      return;
    }
    
    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      // Initialize component data
      this.curentyear = this.globalService.tax_year || '';
      this.scenario = this.globalService.scenario || '';
      this.jcd_key = this.globalService.jcd_key || '';
      
      // Check lock status
      this.checkLockStatus();
      
      // Load tax year dropdown
      this.getTaxYearDropDown();
    } catch (e) {
      console.error('Error in form-1118-sync init:', e);
    }
  }

  checkLockStatus(): void {
    const lockparams = {
      "action_code": 'n10cpv',
      "scenario": this.scenario 
    };
    
    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      lockparams,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.is_locked = data.jsonObject;
        if (this.is_locked != undefined && this.is_locked != null && 
            this.is_locked[0]?.LOCK_STATUS == 'Y') {
          this.gtwSnackbarService.openSnackBar(
            `Tax Return has already been filed for tax year ${this.filterParams.tax_year || this.curentyear}.`,
            'gtw-snackbar--red'
          );
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
        this.cdr.detectChanges();
      }
    );
  }

  getTaxYearDropDown(): void {
    // 🚀 CRITICAL: Build params with all required fields for action code 'e83e1x'
    const params: any = {
      "action_code": 'e83e1x',
      "tax_year": this.filterParams?.tax_year || this.globalService?.tax_year || this.globalService?.globalParams?.tax_year || '',
      "scenario": this.filterParams?.scenario || this.globalService?.scenario || this.globalService?.globalParams?.scenario || '',
      "jcd_key": this.filterParams?.jcd_key || this.globalService?.jcd_key || this.globalService?.globalParams?.jcd_key || '',
      "screen_key": this.filterParams?.screen_key || this.globalService?.screen_key || this.globalService?.globalParams?.screen_key || '',
      "jcd_ta_key": this.filterParams?.jcd_ta_key || this.globalService?.jcd_ta_key || this.globalService?.globalParams?.jcd_ta_key || '',
      "activity_key": this.filterParams?.activity_key || this.globalService?.activity_key || this.globalService?.globalParams?.activity_key || '',
      "screen_type": this.filterParams?.screen_type || this.globalService?.screen_type || this.globalService?.globalParams?.screen_type || 'custom',
      "is_locked": this.filterParams?.is_locked || this.globalService?.is_locked || this.globalService?.globalParams?.is_locked || 'N',
      "issue_id": this.filterParams?.issue_id || this.globalService?.issue_id || this.globalService?.globalParams?.issue_id || '',
      "issue_short_desc": this.filterParams?.issue_short_desc || this.globalService?.issue_short_desc || this.globalService?.globalParams?.issue_short_desc || '',
      "issue_id_for_tx_save": this.filterParams?.issue_id_for_tx_save || this.globalService?.issue_id_for_tx_save || this.globalService?.globalParams?.issue_id_for_tx_save || '',
      "scenarioCode": this.filterParams?.scenarioCode || this.filterParams?.scenario_code || this.globalService?.scenarioCode || this.globalService?.scenario_code || 'RAF',
      "is_issue_key_enabled": this.filterParams?.is_issue_key_enabled || this.globalService?.is_issue_key_enabled || this.globalService?.globalParams?.is_issue_key_enabled || 'N'
    };
    
    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.taxYearData = _.filter(data.jsonObject, (p: any) => {
          return _.includes(['TAX_YEAR'], p.CODE_GROUP);
        });
        
        this.filteredTaxYearData = [];
        for (let indx in this.taxYearData) {
          if (this.taxYearData[indx].CODE_GROUP == 'TAX_YEAR' &&
              (this.taxYearData[indx].CODE_DISP_DESC <= this.curentyear && 
               this.taxYearData[indx].CODE_DISP_DESC >= (parseInt(this.curentyear) - 5))) {
            this.filteredTaxYearData.push(this.taxYearData[indx]);
          }
        }
        // Sort in descending order (newest first) like AngularJS orderBy:'-CODE_DISP_DESC'
        this.filteredTaxYearData.sort((a, b) => {
          return parseInt(b.CODE_DISP_DESC) - parseInt(a.CODE_DISP_DESC);
        });
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading tax year dropdown:', error);
        this.cdr.detectChanges();
      }
    );
  }

  getScenarioDropDown(): void {
    this.scenarioDropDown = [];
    this.selectedScenario = "";
    this.filingGroupDropDown = [];
    this.selectedFilingGroup = "";
    this.filingEnityDetails = [];
    this.filerEntity = "";
    this.showFilerEntity = false;
    
    if (this.selectedTaxYear == undefined || this.selectedTaxYear == null || this.selectedTaxYear == '') {
      this.gtwSnackbarService.openSnackBar("Please select the Tax Year to get the Scenario.", 'gtw-snackbar--red');
      return;
    }

    const params = {
      "action_code": '7q0wiy',
      "tax_year": this.selectedTaxYear
    };
    const mergedParams = _.merge({}, this.globalService, params);
    
    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      mergedParams,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.scenarioDropDown = data.jsonObject;
        // Sort by VALUE like AngularJS orderBy:'VALUE'
        this.scenarioDropDown.sort((a, b) => {
          return a.VALUE - b.VALUE;
        });
        this.showscenario = true;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading scenario dropdown:', error);
        this.cdr.detectChanges();
      }
    );
  }

  getFilingGroupDropDown(): void {
    this.filingGroupDropDown = [];
    this.selectedFilingGroup = "";
    this.filingEnityDetails = [];
    this.filerEntity = "";
    this.showFilerEntity = false;
    
    // 🚀 CRITICAL: Build params with all required fields for action code '0udsuc'
    const params: any = {
      "action_code": '0udsuc',
      "tax_year": this.selectedTaxYear,
      "scenario": this.selectedScenario,
      "jcd_key": this.filterParams?.jcd_key || this.globalService?.jcd_key || this.globalService?.globalParams?.jcd_key || '',
      "screen_key": this.filterParams?.screen_key || this.globalService?.screen_key || this.globalService?.globalParams?.screen_key || '',
      "jcd_ta_key": this.filterParams?.jcd_ta_key || this.globalService?.jcd_ta_key || this.globalService?.globalParams?.jcd_ta_key || '',
      "activity_key": this.filterParams?.activity_key || this.globalService?.activity_key || this.globalService?.globalParams?.activity_key || '',
      "screen_type": this.filterParams?.screen_type || this.globalService?.screen_type || this.globalService?.globalParams?.screen_type || 'custom',
      "is_locked": this.filterParams?.is_locked || this.globalService?.is_locked || this.globalService?.globalParams?.is_locked || 'N',
      "issue_id": this.filterParams?.issue_id || this.globalService?.issue_id || this.globalService?.globalParams?.issue_id || '',
      "issue_short_desc": this.filterParams?.issue_short_desc || this.globalService?.issue_short_desc || this.globalService?.globalParams?.issue_short_desc || '',
      "issue_id_for_tx_save": this.filterParams?.issue_id_for_tx_save || this.globalService?.issue_id_for_tx_save || this.globalService?.globalParams?.issue_id_for_tx_save || ''
    };
    
    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.filingGroupDropDown = data.jsonObject;
        // Sort by VALUE like AngularJS orderBy:'VALUE'
        this.filingGroupDropDown.sort((a, b) => {
          return a.VALUE - b.VALUE;
        });
        this.showFilingGroup = true;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading filing group dropdown:', error);
        this.cdr.detectChanges();
      }
    );
  }

  filingEnityDetailInfo(): void {
    this.filingEnityDetails = [];
    this.filerEntity = "";
    this.showFilerEntity = false;
    
    // 🚀 CRITICAL: Build params with all required fields for action code '35gc5l'
    const params: any = {
      "action_code": "35gc5l",
      "tax_year": this.selectedTaxYear,
      "scenario": this.selectedScenario,
      "filing_group_key": this.selectedFilingGroup,
      "jcd_key": this.filterParams?.jcd_key || this.globalService?.jcd_key || this.globalService?.globalParams?.jcd_key || '',
      "screen_key": this.filterParams?.screen_key || this.globalService?.screen_key || this.globalService?.globalParams?.screen_key || '',
      "jcd_ta_key": this.filterParams?.jcd_ta_key || this.globalService?.jcd_ta_key || this.globalService?.globalParams?.jcd_ta_key || '',
      "activity_key": this.filterParams?.activity_key || this.globalService?.activity_key || this.globalService?.globalParams?.activity_key || '',
      "screen_type": this.filterParams?.screen_type || this.globalService?.screen_type || this.globalService?.globalParams?.screen_type || 'custom',
      "is_locked": this.filterParams?.is_locked || this.globalService?.is_locked || this.globalService?.globalParams?.is_locked || 'N',
      "issue_id": this.filterParams?.issue_id || this.globalService?.issue_id || this.globalService?.globalParams?.issue_id || '',
      "issue_short_desc": this.filterParams?.issue_short_desc || this.globalService?.issue_short_desc || this.globalService?.globalParams?.issue_short_desc || '',
      "issue_id_for_tx_save": this.filterParams?.issue_id_for_tx_save || this.globalService?.issue_id_for_tx_save || this.globalService?.globalParams?.issue_id_for_tx_save || ''
    };
    
    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.filerEntity = null;
        this.filingAllDetails = Object.assign([], data.jsonObject);
        this.filingEnityDetails = Object.assign([], this.filingAllDetails);
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading filing entity details:', error);
        this.cdr.detectChanges();
      }
    );
  }

  showSyncButton(): void {
    this.showFilerEntity = true;
    this.cdr.detectChanges();
  }

  sync1118Process(): void {
    // Validation
    if (this.selectedTaxYear == undefined || this.selectedTaxYear == null || this.selectedTaxYear == '') {
      this.gtwSnackbarService.openSnackBar("Please select a valid Tax Year.", 'gtw-snackbar--red');
      return;
    }
    if (this.selectedScenario == undefined || this.selectedScenario == null || this.selectedScenario == '') {
      this.gtwSnackbarService.openSnackBar("Please select a valid Scenario.", 'gtw-snackbar--red');
      return;
    }
    if (this.selectedFilingGroup == undefined || this.selectedFilingGroup == null || this.selectedFilingGroup == '') {
      this.gtwSnackbarService.openSnackBar("Please select a valid Filing Group.", 'gtw-snackbar--red');
      return;
    }
    if (this.filerEntity == undefined || this.filerEntity == null || this.filerEntity == '') {
      this.gtwSnackbarService.openSnackBar("Please select a valid Filing Entity.", 'gtw-snackbar--red');
      return;
    }
    
    // Show confirmation dialog - create config directly
    this.modalConfig = {
      title: 'Sync process for Form-1118',
      message: 'Are you sure to initiate the Sync process for Form-1118?',
      iconClass: 'fa fa-sign-out',
      iconColor: 'txt-color-orange-dark',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-default',
      showCancelButton: true,
      showConfirmButton: true,
      animationClass: 'animated fadeIn fast',
      loading: false,
      loadingText: 'Processing...'
    };
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirmSync(): void {
    this.showConfirmDialog = false;
    this.initiate1118Sync();
  }

  onCancelConfirm(): void {
    this.showConfirmDialog = false;
    this.cdr.detectChanges();
  }

  initiate1118Sync(): void {
    const filerEntityDetail = this.filingEnityDetails.find(item => item.FILER_LE_KEY == this.filerEntity);
    
    // Build lockInfo object matching the required format
    const lockInfo = {
      "tax_year": this.selectedTaxYear,
      "scenario": this.selectedScenario,
      "jcd_key": this.jcd_key,
      "consol_group_key": this.selectedFilingGroup,
      "combination_key": filerEntityDetail?.FILER_COMBINATION_KEY || ''
    };
    
    // Build params matching the exact payload format
    const params: any = {
      "sso_id": this.USER_SETTINGS?.user?.sso_id || '',
      "tax_year": this.selectedTaxYear,
      "scenario": this.selectedScenario,
      "jcd_key": this.jcd_key,
      "fg_Key": this.selectedFilingGroup,
      "filer_le_key": this.filerEntity,
      "filer_le_label": filerEntityDetail?.FILER_LE || '',
      "checkLockedbj": [lockInfo]  // Array, not stringified - Angular will JSON.stringify the entire payload
    };
    
    this.form1118SyncService.initiate1118Sync(
      this.GENERAL_CONFIG.pdf_engine_api_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess != 1) {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
          return;
        }
        this.gtwSnackbarService.openSnackBar(
          "Sync Process for Form 1118 is initiated. Email will be sent, after the process is complete.",
          'gtw-snackbar--green'
        );
        // Emit refresh event for AngularJS integration
        this.requestGridUpdate.emit('refresh');
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error initiating 1118 sync:', error);
        this.gtwSnackbarService.openSnackBar(
          "Error occurred while initiating sync process. Please try again.",
          'gtw-snackbar--red'
        );
        this.cdr.detectChanges();
      }
    );
  }

  cancel(): void {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }
}

